import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import SearchPanel, { ValueChangedEvent } from "@widgets/SearchPanel";
import { fnGetDataSet } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import SnResizable from "@shared/components/SnResizable";
import usePanelSizes from "@shared/hooks/usePanelSizes";
import { showPopupModal } from "@widgets/CommonModal";
import SN_COS_MAS_REG_004_POP_01 from "@menu/COS/SN_COS_MAS_REG_004/SN_COS_MAS_REG_004_POP_01";

interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string; // 행의 고유 ID
  [key: string]: any;
}

/** 요소비용집계 버튼(sys_headerctrl)이 달리는 cd_form. getInitForm 이 이 키로 내려준다. */
const HEADER_FORM = "grid2";

const SN_COS_MAS_REG_004 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridApiRef1 = useRef<any>(null);
  const gridApiRef2 = useRef<any>(null);
  const searchRef = useRef<any>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { userInfo, config } = global();
  //#endregion

  //#region panel 사이즈 관련
  const cd_menu = Menuprops.cd_menu || "SN_COS_MAS_REG_004";
  const defaultSize = {
    panel1width: "49.5%",
    panel2width: "49.5%",
  };
  const [sizes, onResizeStop] = usePanelSizes(cd_menu, defaultSize); // SnResizable들의 크기
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns1, setColums1] = useState<any[]>([]);
  const [columns2, setColums2] = useState<any[]>([]);
  const [dataSource1, setDataSource1] = useState<any[]>([]);
  const [dataSource2, setDataSource2] = useState<any>();
  const [gridFocus, setGridFocus] = useState(false);
  /** 상단 버튼 메타. getInitForm 이 이미 {controls, data} 로 가공해 주므로 다시 감싸지 않는다. */
  const [headerInfo, setHeaderInfo] = useState<any>({ controls: [], data: {} });

  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      setDataSource1([]);
      setDataSource2([]);
      try {
        // 평가기간 기본값 = 최신 마감월(공통 메타 쿼리)
        const _rtnYm: any = await fnGetDataSet({
          id: "com_CommonMapper_12",
          params: { cd_firm: userInfo.cd_firm, cd_biz: userInfo.cd_biz },
        });
        const _dt = Array.isArray(_rtnYm?.[0]) ? _rtnYm[0] : [];
        const newData = _dt?.[0]
          ? { dt_from: _dt[0].ym_start, dt_to: _dt[0].ym }
          : {};
        const rtn: any = await getInitFormData();
        setFormData({ ...rtn.formData, ...newData });
        setFormControlData(rtn.formControlData);
        setColums1(rtn.grid.grid1); // H그리드 항목 설정
        setColums2(rtn.grid.grid2); // L그리드 항목 설정
        setHeaderInfo(rtn.header?.[HEADER_FORM] ?? { controls: [], data: {} });
      } catch (error) {}
    };
    fetchform();
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  useEffect(() => {
    gridApiRef1.current.selectAndFocusRow(0);
    setGridFocus(false);
  }, [gridFocus]);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
  }));
  //region onSearch
  const onSearch = async () => {
    try {
      const params = {
        id: "sn_cos_mas_reg_004_01",
        params: searchRef.current.getFormdata(),
      };

      setDataSource1([]);
      setDataSource2([]);
      const data: any = await fnGetDataSet(params);
      setDataSource1(data[0]);
      setDataSource2(data[1]);

      if (data[0] && data[0].length > 0) {
        await new Promise((resolve) => setTimeout(resolve, 50));

        setGridFocus(true);
      }

      gridApiRef1.current.resetUpdate();
      gridApiRef2.current.resetUpdate();
    } catch (error: any) {}
  };
  //#region  Btn
  /**
   * 요소비용집계 버튼.
   * 프로젝트별 원가계산을 쓰는 회사(sys_config PMS0003='Y')는 이 메뉴로 집계하면 안 된다.
   * 프로젝트별 요소비용집계(SN_PMS_CLS_REG_002)에서 프로젝트 단위로 집계해야 하므로 막는다.
   * 그 외에는 비용집계 팝업을 띄운다(API 가져오기 → 임시저장 → 비용집계).
   */
  const onGrid1BtnsClick = async (id?: string) => {
    // 헤더 버튼(sys_headerctrl)은 cd_param 을 sender 로 넘긴다.
    // 이 그리드의 유일한 버튼이 요소비용집계이므로 cd_param 이 비어 있어도 실행한다.
    if (id && id !== "btnApply") return;

    // 통제환경은 로그인 시 적재된 config 에서 읽는다. 미설정이면 'N'.
    if (String(config?.PMS0003?.dc_ctrl ?? "N").trim().toUpperCase() === "Y") {
      await showDialog(
        "alter",
        "요소비용집계는 프로젝트별 요소비용집계메뉴에서 해야합니다."
      );
      return;
    }

    const searchForm = searchRef.current?.getFormdata?.() ?? {};
    if (!searchForm.cd_biz || !String(searchForm.dt_to ?? "").trim()) {
      showDialog("alter", "사업장과 평가기간을 확인해 주세요.");
      return;
    }

    const result = await showPopupModal({
      title: "비용집계",
      children: (
        <SN_COS_MAS_REG_004_POP_01
          selectedData={{
            cd_biz: searchForm.cd_biz,
            dt_from: searchForm.dt_from,
            dt_to: searchForm.dt_to,
          }}
        />
      ),
      width: "80%",
      height: "80vh",
      okayBtn: false, // 확인 버튼 없음 — 집계는 그리드 헤더의 "3. 비용집계" 가 한다
    });
    // 집계하고 닫혔을 때만 재조회한다(그냥 닫으면 undefined).
    if (result?.aggregated) await onSearch();
  };

  const onSearchValueChanged = (event: ValueChangedEvent) => {
    if (event.source !== "popup") return;
    if (event.colId !== "dt_from" && event.colId !== "dt_to") return;

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      searchTimerRef.current = null;
      onSearch();
    }, 0);
  };

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          onValueChanged={onSearchValueChanged}
        ></SearchPanel>
      </div>
      {/* 요소비용집계 버튼은 sys_headerctrl 메타로 계정(우측=grid2) 그리드 상단에 붙는다 */}
      <div className="flex h-full w-full space-x-2">
        <SnResizable
          direction={"width"}
          seq={1}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel1width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
        >
          <SnGrid
            ref={gridApiRef1} // Grid Name
            columnConfig={columns1} // 컬럼 설정 전달
            rowData={dataSource1} // DataSource
            etcProps={{
              rowSelection: "single", // 행 선택 모드
              isEditable: false, // 집계 결과 표시 전용 — 직접 수정 불가
              title: "요소비용", // 그리드 타이틀
            }}
            // 좌(요소비용) 행 선택 시 우(계정) 그리드를 부문+요소코드로 필터
            filter={{
              refs: [gridApiRef2],
              columns: ["cd_cc", "cd_elem"],
            }}
          />
        </SnResizable>
        <SnResizable
          direction={"width"}
          seq={2}
          cd_menu={cd_menu}
          defaultSize={defaultSize}
          size={sizes.panel2width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
        >
          <SnGrid
            ref={gridApiRef2} // Grid Name
            columnConfig={columns2} // 컬럼 설정 전달
            rowData={dataSource2} // DataSource
            etcProps={{
              rowSelection: "single",
              isEditable: false, // 집계 결과 표시 전용 — 직접 수정 불가
              title: "계정", // 그리드 타이틀
            }}
            events={{ onBtnEtcClick: onGrid1BtnsClick }} // 요소비용집계 (sys_headerctrl)
            headerInfo={headerInfo}
          />
        </SnResizable>
      </div>
    </div>
  );
  //#endregion
});

export default SN_COS_MAS_REG_004;
